import { withTranslation } from "next-i18next";
import { useRouter } from "next/compat/router";
import Modal from "@components/core/Modal";
import Button from "@components/core/Button";
import Image from "@components/core/Image";
import { useCurrentUser } from "@hooks/use-current-user";
import { putLanguage } from "@utils/call-api/users-call";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import ahoy from "@lib/ahoy";
import { analytics } from "@lib/trackers/analytics";

function UserLangModal({ t }) {
  const user = useCurrentUser();
  const router = useRouter();

  const userLang = user?.user_tags?.find((tag) => tag.key == "lang")?.value || "fr";
  const [showModal, setShowModal] = useState(false);

  const flagSrc = (locale) => {
    switch (locale) {
      case "fr":
        return "franceFlag";
      case "de":
        return "germanyFlag";
      case "nl":
        return "netherlandsFlag";
      case "en":
        return "unitedKingdomFlag";
      default:
        return "franceFlag";
    }
  };

  const flagImage = (locale) => {
    const src = flagSrc(locale);
    return (
      <Image
        src={src}
        alt={src}
      />
    );
  };

  function sendEvent(button) {
    const eventData = { button, page: router.asPath, user_lang: userLang, locale: router.locale };

    analytics.track("userlangmodal", eventData);
    ahoy("userlangmodal", eventData);
  }

  function changeLocaleToUserLang() {
    sendEvent("UserLangModal.redirect_btn");

    Cookies.set("NEXT_LOCALE", userLang, { expires: 3650 });

    const { pathname, asPath, query } = router;
    router.push({ pathname, query }, asPath, { locale: userLang });
  }

  async function changeUserLang(newLang) {
    setShowModal(false);
    await putLanguage(newLang);

    Cookies.set("NEXT_LOCALE", newLang, { expires: 3650 });
    sendEvent("UserLangModal.change_lang_btn");
    router.reload();
  }

  useEffect(() => {
    setShowModal(user && userLang !== router?.locale);
  }, [user, userLang, router?.locale]);

  if (router?.locales?.length < 2) return null;

  return (
    <Modal
      show={showModal}
      closeButton={false}
      style={{ zIndex: 3000 }}
    >
      <div className="p-3">
        <div className="container pt-4 text-center">
          <h5>{t("user_lang_modal.title", { lng: userLang })}</h5>

          <p>{t("user_lang_modal.ask_for_redirection", { lng: userLang })}</p>

          <Button
            variant="link-primary"
            onClick={changeLocaleToUserLang}
            title={t("user_lang_modal.redirect_lang_btn", { lng: userLang })}
          >
            {flagImage(userLang)}
            <span className="px-2">
              {t("user_lang_modal.redirect_lang_btn", { lng: userLang })}
            </span>
          </Button>
        </div>

        <hr className="mx-4 mb-4 border border-middle-gray" />

        <div className="container pb-3 text-center">
          <h5>{t("user_lang_modal.title")}</h5>

          <p>{t(`user_lang_modal.ask_change_lang.${userLang}`)}</p>

          <Button
            variant="link-primary"
            onClick={() => changeUserLang(router.locale)}
            title={t("user_lang_modal.change_lang_btn")}
          >
            {flagImage(router?.locale)}
            <span className="px-2">{t("user_lang_modal.change_lang_btn")}</span>
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default withTranslation("common")(UserLangModal);
